<script setup>
import api from '../helpers/api'
import { ref } from 'vue'
import {$cart, $isCartOpen} from '../store'
import { useStore } from '@nanostores/vue'
import Spinner from '@components/Spinner.vue'
import BaseModal from '@components/BaseModal.vue'
import BaseInput from '@components/BaseInput.vue'
import axios from 'axios'
import { generateRecaptchaToken } from '@helpers/utils'

const cart = useStore($cart);
const emit = defineEmits(['added'])

defineOptions({
  inheritAttrs: false
})

const props = defineProps({
  payload: {
    type: [Object, Array],
    required: true,
  },
  listing: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  notify: {
    type: Boolean,
    default: true,
  },
})

const addingToCart = ref(false)
const notifyMeOpen = ref(false)
const notifyForm = ref({
  email: '',
  optin: true,
})
const notifying = ref(false)

async function addToCart() {
  if (props.disabled) {
    if (props.notify) {
      notifyMeOpen.value = true
    }
    return
  }

  const requests = []
  const payloads = Array.isArray(props.payload) ? props.payload : [props.payload]

  window.criteo_q = window?.criteo_q || [];
  window.criteo_q.push(
    { event: 'setAccount', account: 94147 },
    { event: 'setSiteType', type: /iPad/.test(navigator.userAgent) ? 't' : /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(navigator.userAgent) ? 'm' : 'd'},
  )

  let page = 'PDP'
  if (window.location.pathname === '/') {
    page = 'Home'
  }
  if (window.location.pathname === '/quiz') {
    page = 'Quiz'
  }
  if (window.location.pathname.startsWith('/c/') || window.location.pathname.includes('/shop')) {
    page = 'PLP'
  }

  addingToCart.value = true

  payloads.forEach(payload => {
    let product = props.listing.products.find(({ id }) => id === payload?.product_id)

    let quantity = payload?.quantity || 1

    window?.dataLayer?.push({ ecommerce: null })
    window?.dataLayer?.push({
      event: 'add_to_cart',
      cart_action: payload?.listing_plan_id ? 'Subscribe' : 'Single',
      ecommerce: {
        currency: 'USD',
        items: [{
          item_id: props.listing.id.toString(),
          item_name: props.listing.title,
          item_brand: props.listing?.product_line?.brand?.title || '',
          item_list_id: page,
          item_list_name: page,
          price: Number(product?.price || 0).toString(),
          quantity: Number(quantity).toString(),
        }]
      },
    })

    window?.sendFbEvent('AddToCart', {
      currency: 'USD',
      value: product?.price || 0,
      content_type: 'product',
      content_ids: [product?.id?.toString()],
      contents: [{
        id: product?.id?.toString(),
        item_price: product?.price || 0,
        quantity,
      }],
    })

    window.criteo_q.push({
      event: 'addToCart',
      item: [
        {id: props.listing.id, price: product?.price || 0, quantity }
      ]
    })

    requests.push(api.post(`/carts/${cart.value.id}/add`, payload).then(({ data }) => data.data))
  })

  const responses = await axios.all(requests)

  const newCart = requests.length > 1
    ? await api.get(`/carts/${cart.value.id}`).then(({ data }) => data.data)
    : responses[0]

  $cart.set(newCart)
  addingToCart.value = false

  $isCartOpen.set(true)

  emit('added')
}

async function submitNotify() {
  notifying.value = true

  await axios.post(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${import.meta.env.PUBLIC_KLAVIYO_API_KEY}`,
      {
        'data': {
          'type': 'back-in-stock-subscription',
          'attributes': {
            'profile': {
              'data': {
                'type': 'profile',
                'attributes': {
                  'email': notifyForm.value.email,
                }
              }
            },
            'channels': ['EMAIL'],
          },
          'relationships': {
            'variant': {
              'data': {
                'type': 'catalog-variant',
                'id': `$custom:::$default:::${props.listing.id}`
              }
            }
          }
        }
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'revision': '2023-12-15'
        }
      }
  )

  if (notifyForm.value.optin) {
    const captcha = await generateRecaptchaToken('subscribe')

    await api.post(`/subscribe`, {
      email: notifyForm.value.email,
      list_id: 'WQh9NS',
      captcha,
    })
  }

  notifying.value = false
  notifyMeOpen.value = false
}
</script>

<template>
  <button type="button" :disabled="addingToCart" @click="addToCart" v-bind="$attrs">
    <Spinner v-if="addingToCart" color="white" width="18px" />
    <span v-else-if="disabled && notify">Out of Stock - Notify Me</span>
    <span v-else>
      <slot />
    </span>
  </button>

  <BaseModal v-model="notifyMeOpen" title="Never miss a restock!" class="notify" :max-width="500" dismissable>
    <div class="notify__description">We’ll notify you when this item is back in stock.</div>

    <form @submit.prevent="submitNotify">
      <base-input type="email" label="Email address" v-model="notifyForm.email" required />

      <label class="notify__opt">
        <input type="checkbox" v-model="notifyForm.optin" />
        Opt in for promotional material, & exclusive offers
      </label>

      <button type="submit" :disabled="notifying" class="notify__button">
        <Spinner v-if="notifying" color="white" width="18px" />
        <span v-else>NOTIFY ME</span>
      </button>
    </form>
  </BaseModal>
</template>

<style lang="scss" scoped>
button:disabled {
  opacity: 0.8;
  cursor: not-allowed!important;
}

.notify {
  :deep(.modal__heading) {
    margin-bottom: 4px!important;
  }

  .notify__description {
    color: var(--Neutral-1000, #3C3833);
    font-family: Outfit, 'Outfit Fallback', Arial, serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 32px; /* 228.571% */
    margin-bottom: 12px;

    @media screen and (min-width: 768px) {
      font-size: 18px;
      margin-bottom: 24px;
    }
  }

  .notify__opt {
    display: flex;
    align-items: center;
    color: var(--Neutral-700, #767370);
    font-family: Poppins, 'Poppins Fallback', 'Trebuchet MS Fallback', Arial, serif;
    font-size: 10px;
    font-weight: 400;
    line-height: 12px; /* 120% */
    margin-top: 12px;
    cursor: pointer;
    gap: 9px;

    @media screen and (min-width: 768px) {
      font-size: 14px;
      line-height: 24px;
      margin-top: 24px;
      gap: 18px;
    }

    input {
      accent-color: var(--Primary-500, #183D4F);
      border-radius: 1.2px;

      @media screen and (min-width: 768px) {
        height: 20px;
        width: 20px;
      }
    }
  }

  .notify__button {
    border-radius: 74px;
    background: var(--Orange---terracotta, #DB7E5A);
    color: #FFF;
    font-family: Outfit, 'Outfit Fallback', Arial, serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px; /* 125% */
    letter-spacing: 1px;
    text-transform: uppercase;
    border: 0;
    width: 100%;
    padding: 12px;
    margin-top: 12px;

    @media screen and (min-width: 768px) {
      font-size: 20px;
      padding: 16px;
      margin-top: 24px;
    }
  }
}
</style>
